import React from "react";
import { Button } from "../common/Button";
import { useFunnelNavigation } from "../../hooks/useFunnelNavigation";
import { useFunnel } from "../../context/FunnelContext";
import { StepHeader } from "../common/StepHeader";

const HEALTH_CONCERNS_MAP = {
  hypertension: "Hypertension",
  high_cholesterol: "High cholesterol",
  obesity: "Obesity",
  diabetes: "Diabetes",
  heart_disease: "Heart disease",
  cancer: "Cancer",
  lung_disease: "Lung disease",
  thyroid_disease: "Thyroid disease",
  gastric_disease: "Gastric disease",
  none: "✅ None of the above",
};

export function Summary({ config }) {
  const { nextStep } = useFunnelNavigation();
  const { state } = useFunnel();
  const { userData } = state;

  // Calculate BMI
  const calculateBMI = () => {
    if (!userData.height || !userData.weight) return null;

    let heightInM;
    let weightInKg;

    // Convert height to meters
    if (userData.height.unit === "imperial") {
      const totalInches =
        Number(userData.height.feet) * 12 + Number(userData.height.inches);
      heightInM = totalInches * 0.0254;
    } else {
      heightInM = Number(userData.height.cm) / 100;
    }

    // Convert weight to kg
    if (userData.weight.unit === "imperial") {
      weightInKg = Number(userData.weight.weight) * 0.453592;
    } else {
      weightInKg = Number(userData.weight.weight);
    }

    const bmi = weightInKg / (heightInM * heightInM);
    return bmi.toFixed(1);
  };

  // Calculate weight difference
  const calculateWeightDifference = () => {
    if (!userData.weight || !userData.weightGoal) return null;

    let currentWeight =
      userData.weight.unit === "imperial"
        ? Number(userData.weight.weight)
        : Number(userData.weight.weight) * 2.20462;

    let targetWeight =
      userData.weightGoal.unit === "imperial"
        ? Number(userData.weightGoal.weight)
        : Number(userData.weightGoal.weight) * 2.20462;

    return Math.abs(currentWeight - targetWeight).toFixed(1);
  };

  const getActivityLevelText = () => {
    const activityMap = {
      sedentary: "Sedentary",
      moderate: "Moderately active",
      very_active: "Very active",
    };
    return activityMap[userData.activityLevel] || "";
  };

  const getTrainingActivityText = () => {
    const trainingMap = {
      none: "None",
      lifting: "Lifting",
      cardio: "Cardio",
      cardio_and_lifting: "Cardio & Lifting",
    };
    return trainingMap[userData.trainingActivity] || "";
  };

  return (
    <div className="space-y-4 px-4">
      <StepHeader
        title={config.title}
        subtitle={config.subtitle}
        order={config.headerOrder}
      />

      <div className="bg-white rounded-2xl p-6 space-y-4">
        {/* BMI Section */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-800 font-semibold">Body Mass Index</span>
            <span className="text-5xl font-bold text-gray-900">
              {calculateBMI()}
            </span>
          </div>
          <BMIScale bmi={Number(calculateBMI())} />
        </div>

        <div className="space-y-4 pt-4 border-t border-gray-100">
          {/* Weight Goal Section */}
          <div className="flex justify-between items-center">
            <span className="text-gray-600">
              <span className="mr-2">🎯</span>
              Target Weight Difference
            </span>
            <span className="font-semibold text-gray-900">
              {calculateWeightDifference()} lbs
            </span>
          </div>

          {/* Activity Level Section */}
          <div className="flex justify-between items-center">
            <span className="text-gray-600">
              <span className="mr-2">🤸‍♀️</span>
              Activity Level
            </span>
            <span className="font-semibold text-gray-900">
              {getActivityLevelText()}
            </span>
          </div>

          {/* Exercise Section */}
          <div className="flex justify-between items-center">
            <span className="text-gray-600">
              <span className="mr-2">🏃‍♀️</span>
              Exercise
            </span>
            <span className="font-semibold text-gray-900">
              {getTrainingActivityText()}
            </span>
          </div>

          {/* Health Concerns Section */}
          {userData.healthConcerns && userData.healthConcerns.length > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">
                <span className="mr-2">💊</span>
                Health Concerns
              </span>
              <span className="font-semibold text-gray-900">
                {userData.healthConcerns
                  .map((id) => HEALTH_CONCERNS_MAP[id])
                  .join(", ")}
              </span>
            </div>
          )}
        </div>
      </div>

      <Button onClick={nextStep} className="w-full">
        Continue
      </Button>
    </div>
  );
}

const BMIScale = ({ bmi }) => {
  const getPointerPosition = (bmi) => {
    // Clamp BMI between 15 and 35 for visualization purposes
    const clampedBMI = Math.min(Math.max(bmi, 15), 35);
    // Convert BMI to percentage position (15-35 range maps to 0-100%)
    return ((clampedBMI - 15) / 20) * 100;
  };

  return (
    <div className="w-full space-y-3">
      <div className="relative h-3 rounded-full overflow-hidden flex">
        {/* BMI color bands */}
        <div className="w-[20%] bg-blue-400"></div>
        <div className="w-[30%] bg-green-400"></div>
        <div className="w-[25%] bg-orange-400"></div>
        <div className="w-[25%] bg-red-400"></div>

        {/* BMI pointer */}
        {bmi && (
          <div
            className="absolute top-0 w-1 h-8 bg-gray-900 transform -translate-x-1/2 shadow-md"
            style={{ left: `${getPointerPosition(bmi)}%` }}
          >
            <div className="absolute -top-7 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
              <span className="text-sm font-bold bg-gray-900 text-white px-2 py-1 rounded-md">
                {bmi}
              </span>
            </div>
          </div>
        )}
      </div>
      {/* Labels */}
      <div className="flex justify-between text-xs text-gray-500 px-1">
        <span>15</span>
        <span>18.5</span>
        <span>25</span>
        <span>30</span>
        <span>35+</span>
      </div>
    </div>
  );
};
